import type { WorkspaceDocument, WorkspaceFolder } from "@/models/workspace/Workspace";
import type Workspace from "@/models/workspace/Workspace";
import { isWorkspaceDocumentHistoric } from "@/pages/workspace/utils/workspaceItemUtils";
import type {
  DocumentIdWithRevisionNumber,
  WorkspaceContext,
} from "@/services/messageService";
import { logger } from "@/lib/logger";

export type ConversationWorkspaceDocumentSnapshot = {
  workspaceId: string;
  documentId: string;
  blobId: string;
  revisionVersion: number;
};

/** Matches API `[MaxLength(2000)]` on create-conversation `WorkspaceDocuments`. */
export const MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS = 2000;

const getAllDocuments = (workspace: Workspace): WorkspaceDocument[] => {
  const collectFromFolders = (folders: WorkspaceFolder[]): WorkspaceDocument[] =>
    folders.flatMap((f) => [...f.documents, ...collectFromFolders(f.folders)]);
  return [...workspace.documents, ...collectFromFolders(workspace.folders)];
};

/**
 * Ordered unique `(workspaceId, documentId)` pairs whose `documentId` resolves to a file in
 * that workspace. Folder-only IDs stored as synthetic context rows do not appear in
 * `getAllDocuments` and are excluded — they never produce snapshot rows.
 */
function buildOrderedResolvableDocumentPairs(
  context: WorkspaceContext,
  workspaces: Workspace[]
): Array<{ workspaceId: string; entry: DocumentIdWithRevisionNumber }> {
  const wsMap = new Map(workspaces.map((w) => [w.id, w]));
  const seen = new Set<string>();
  const out: Array<{
    workspaceId: string;
    entry: DocumentIdWithRevisionNumber;
  }> = [];

  for (const [workspaceId, entries] of Object.entries(context)) {
    const ws = wsMap.get(workspaceId);
    if (!ws) continue;
    const docIds = new Set(getAllDocuments(ws).map((d) => d.id));
    for (const entry of entries) {
      if (!docIds.has(entry.documentId)) continue;
      const key = `${workspaceId}:${entry.documentId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ workspaceId, entry });
    }
  }
  return out;
}

/**
 * Counts unique documents per workspace that become snapshot rows (same IDs as
 * {@link buildConversationWorkspaceDocuments} resolves). Folder placeholder rows are ignored.
 *
 * When `workspaces` is omitted, falls back to counting every distinct `documentId` (legacy:
 * includes synthetic folder rows — prefer passing `workspaces` when available).
 */
export function countDedupedWorkspaceDocumentsInContext(
  context: WorkspaceContext,
  workspaces?: Workspace[]
): number {
  if (workspaces?.length) {
    return buildOrderedResolvableDocumentPairs(context, workspaces).length;
  }
  let total = 0;
  for (const entries of Object.values(context)) {
    total += new Set(entries.map((e) => e.documentId)).size;
  }
  return total;
}

export type ExplorerSelectionEntry = {
  item: WorkspaceDocument | WorkspaceFolder;
  workspaceId: string;
};

/**
 * Unique **files** selected in the explorer (per workspace). Folder nodes are ignored —
 * they are selection UI only; {@link buildConversationWorkspaceDocuments} resolves only
 * real document IDs to snapshots.
 */
export function countDedupedDocumentsInExplorerSelection(
  items: ExplorerSelectionEntry[]
): number {
  const byWorkspace = new Map<string, Set<string>>();
  for (const { item, workspaceId } of items) {
    if (!("activeRevision" in item)) continue;
    if (isWorkspaceDocumentHistoric(item)) continue;
    let set = byWorkspace.get(workspaceId);
    if (!set) {
      set = new Set<string>();
      byWorkspace.set(workspaceId, set);
    }
    set.add(item.id);
  }
  let total = 0;
  for (const set of byWorkspace.values()) {
    total += set.size;
  }
  return total;
}

/**
 * Mirrors explorer selection → store sync: skips historic documents; folders contribute
 * their folder id as a synthetic row (same as chat context dialogs).
 */
export function buildWorkspaceContextFromExplorerSelection(
  items: ExplorerSelectionEntry[]
): WorkspaceContext {
  const newContext: WorkspaceContext = {};
  for (const { item, workspaceId } of items) {
    if ("activeRevision" in item && isWorkspaceDocumentHistoric(item)) {
      continue;
    }
    if (!newContext[workspaceId]) {
      newContext[workspaceId] = [];
    }
    if ("activeRevision" in item) {
      newContext[workspaceId].push({
        documentId: item.id,
        activeRevisionNumber: item.activeRevision,
      });
    } else {
      newContext[workspaceId].push({
        documentId: item.id,
        activeRevisionNumber: undefined,
      });
    }
  }
  return newContext;
}

/**
 * Keeps the first N unique `(workspaceId, documentId)` pairs in iteration order and drops the rest.
 */
export function trimWorkspaceContextToMaxDeduped(
  context: WorkspaceContext,
  max: number = MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS
): { context: WorkspaceContext; trimmedAway: number } {
  const pairs: Array<{
    workspaceId: string;
    entry: DocumentIdWithRevisionNumber;
  }> = [];
  const seen = new Set<string>();
  for (const [workspaceId, entries] of Object.entries(context)) {
    for (const entry of entries) {
      const key = `${workspaceId}:${entry.documentId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push({ workspaceId, entry });
    }
  }
  if (pairs.length <= max) {
    return { context, trimmedAway: 0 };
  }
  const kept = pairs.slice(0, max);
  const next: WorkspaceContext = {};
  for (const p of kept) {
    if (!next[p.workspaceId]) next[p.workspaceId] = [];
    next[p.workspaceId].push(p.entry);
  }
  return { context: next, trimmedAway: pairs.length - max };
}

/**
 * Caps **snapshot-eligible** document pairs; synthetic folder rows are preserved and do not
 * consume the limit (they do not produce API snapshot rows).
 */
export function trimWorkspaceContextToMaxResolvable(
  context: WorkspaceContext,
  workspaces: Workspace[],
  max: number
): { context: WorkspaceContext; trimmedAway: number } {
  const resolvableOrdered = buildOrderedResolvableDocumentPairs(
    context,
    workspaces
  );
  if (resolvableOrdered.length <= max) {
    return { context, trimmedAway: 0 };
  }

  const keptKeys = new Set(
    resolvableOrdered
      .slice(0, max)
      .map((p) => `${p.workspaceId}:${p.entry.documentId}`)
  );
  const trimmedAway = resolvableOrdered.length - max;
  const wsMap = new Map(workspaces.map((w) => [w.id, w]));

  const next: WorkspaceContext = {};
  for (const [workspaceId, entries] of Object.entries(context)) {
    const ws = wsMap.get(workspaceId);
    const docIdSet = ws
      ? new Set(getAllDocuments(ws).map((d) => d.id))
      : null;
    const out: DocumentIdWithRevisionNumber[] = [];
    const seenKey = new Set<string>();

    for (const entry of entries) {
      const key = `${workspaceId}:${entry.documentId}`;
      if (seenKey.has(key)) continue;

      if (!docIdSet) {
        out.push(entry);
        seenKey.add(key);
        continue;
      }

      const isResolvable = docIdSet.has(entry.documentId);
      if (!isResolvable) {
        out.push(entry);
        seenKey.add(key);
        continue;
      }

      if (keptKeys.has(key)) {
        out.push(entry);
        seenKey.add(key);
      }
    }

    if (out.length > 0) {
      next[workspaceId] = out;
    }
  }

  return { context: next, trimmedAway };
}

/** Applies {@link MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS} to an in-memory context. */
export function applyWorkspaceContextCap(
  context: WorkspaceContext,
  workspaces?: Workspace[]
): { context: WorkspaceContext; trimmedAway: number } {
  if (workspaces?.length) {
    const cnt = countDedupedWorkspaceDocumentsInContext(context, workspaces);
    if (cnt <= MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS) {
      return { context, trimmedAway: 0 };
    }
    return trimWorkspaceContextToMaxResolvable(
      context,
      workspaces,
      MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS
    );
  }

  const cnt = countDedupedWorkspaceDocumentsInContext(context);
  if (cnt <= MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS) {
    return { context, trimmedAway: 0 };
  }
  return trimWorkspaceContextToMaxDeduped(
    context,
    MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS
  );
}

/**
 * Per workspace: collapse duplicate `documentId` rows (UI can select the same doc twice).
 * If multiple revision numbers exist for the same doc, keep the **highest** — matches
 * `ConversationWorkspaceDocumentResolver` server behavior.
 */
const dedupeDocumentSelections = (
  entries: DocumentIdWithRevisionNumber[]
): DocumentIdWithRevisionNumber[] => {
  const byDocumentId = new Map<string, DocumentIdWithRevisionNumber[]>();
  for (const e of entries) {
    const list = byDocumentId.get(e.documentId) ?? [];
    list.push(e);
    byDocumentId.set(e.documentId, list);
  }

  const merged: DocumentIdWithRevisionNumber[] = [];
  for (const [documentId, group] of byDocumentId) {
    const nums = group
      .map((x) => x.activeRevisionNumber)
      .filter((n): n is number => typeof n === "number");
    merged.push({
      documentId,
      activeRevisionNumber:
        nums.length > 0 ? Math.max(...nums) : undefined,
    });
  }
  return merged;
};

/**
 * Resolves the authoritative blob ID and revision version for each document in
 * the workspace context using the already-loaded workspace data in the browser.
 *
 * This mirrors ConversationWorkspaceDocumentResolver.Resolve on the server so that
 * the API can persist the snapshot directly without loading WorkspaceViewModels.
 *
 * Resolution rule (matches server logic):
 *   blobId = matchedRevision?.blobId ?? document.blobName
 *   revisionVersion = matchedRevision?.revisionVersion ?? document.activeRevision
 *
 * Duplicate `(workspaceId, documentId)` selections are merged per workspace using the
 * highest requested revision number. The result is capped at
 * {@link MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS}; excess rows are dropped and a
 * warning is logged (matches server validation).
 */
export const buildConversationWorkspaceDocuments = (
  workspaceContext: WorkspaceContext,
  allWorkspaces: Workspace[]
): ConversationWorkspaceDocumentSnapshot[] => {
  const { context: cappedContext, trimmedAway } =
    applyWorkspaceContextCap(workspaceContext, allWorkspaces);
  if (trimmedAway > 0) {
    logger.warn(
      "Workspace document snapshot build received context over server limit (trimmed)",
      {
        operation: "buildConversationWorkspaceDocuments",
        trimmedAway,
        maxAllowed: MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS,
      }
    );
  }

  const result: ConversationWorkspaceDocumentSnapshot[] = [];

  for (const [workspaceId, entries] of Object.entries(cappedContext)) {
    const workspace = allWorkspaces.find((ws) => ws.id === workspaceId);
    if (!workspace) continue;

    const allDocs = getAllDocuments(workspace);
    const deduped = dedupeDocumentSelections(entries);

    for (const { documentId, activeRevisionNumber } of deduped) {
      const doc = allDocs.find((d) => d.id === documentId);
      if (!doc) continue;

      const revision =
        activeRevisionNumber != null
          ? doc.revisions.find((r) => r.revisionVersion === activeRevisionNumber)
          : null;

      result.push({
        workspaceId,
        documentId,
        blobId: revision?.blobId ?? doc.blobName,
        revisionVersion: revision?.revisionVersion ?? doc.activeRevision,
      });
    }
  }

  return result;
};
